import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  joinStatsApi,
  type EvaluationMode,
  type JoinDashboard,
  type JoinHourRow,
  type JoinStatus,
} from '../../api/joinstats.js';
import { Spinner, ErrorBox } from '../../components/ui.js';

/**
 * 신규가입 전환 대시보드. 원본 marketing/join_member/dashboard.php 를 그대로
 * 옮겼다 - 구성(조회 조건 → 지표 카드 6개 → 시간대별 상세 + 우선 확인 시간·
 * 시간대 차트 → 일자별 요약)과 판정 규칙이 원본과 같다.
 *
 * 계산은 전부 서버(joinstats.dashboard.ts)가 한다 - 원본도 PHP 가 끝낸 결과를
 * Vue 에 넘겨 그리기만 했다. 여기서는 그 값을 그리는 일만 한다.
 *
 * 색만 이 앱의 어두운 테마 토큰으로 바꿨다. 원본은 흰 배경 전용이라 그대로
 * 가져오면 이 앱 안에서 혼자 하얗게 뜬다.
 */

const HOURS = Array.from({ length: 24 }, (_, i) => i);

type ViewMode = 'all' | 'issue';
type ChartMode = 'conversion' | 'inflow' | 'average';

/** 원본 formatNumber */
const fmtNum = (v: number | null | undefined) => (Number(v) || 0).toLocaleString('ko-KR');

/** 원본 formatRate - 소수 한 자리, .0 은 떼고, 값이 없으면 '-' */
function fmtRate(v: number | null | undefined): string {
  if (v === null || v === undefined) return '-';
  return `${Number(v).toFixed(1).replace(/\.0$/, '')}%`;
}

/** 원본 formatAverage */
function fmtAvg(v: number | null | undefined): string {
  return (Number(v) || 0).toFixed(1).replace(/\.0$/, '');
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoISO(days: number): string {
  return new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
}

/** 원본 :74 - 기본 기간은 5일 전 ~ 오늘 */
const DEFAULTS = { minUsers: '30', warnRate: '30', mode: 'hourly_average' as EvaluationMode };

export function JoinDashboardPage() {
  const [params, setParams] = useSearchParams();

  const from = params.get('from') ?? daysAgoISO(5);
  const to = params.get('to') ?? todayISO();
  const minUsers = params.get('minUsers') ?? DEFAULTS.minUsers;
  const warnRate = params.get('warnRate') ?? DEFAULTS.warnRate;
  const mode = (params.get('mode') ?? DEFAULTS.mode) as EvaluationMode;

  // 원본처럼 칸을 고쳐도 바로 조회하지 않는다 - [조회] 또는 [시간대별 평균] 을
  // 눌러야 반영된다. 판정 방식이 결과를 통째로 바꾸기 때문에, 기준을 다 맞춘
  // 뒤 방식을 골라 한 번에 보는 흐름이 원본의 의도다.
  const [draft, setDraft] = useState({ from, to, minUsers, warnRate });

  const dash = useQuery({
    queryKey: ['join-dashboard', from, to, minUsers, warnRate, mode],
    queryFn: () => joinStatsApi.dashboard({ from, to, minUsers, warnRate, mode }),
  });

  function search(nextMode: EvaluationMode) {
    setParams(
      {
        from: draft.from,
        to: draft.to,
        minUsers: String(draft.minUsers),
        warnRate: String(draft.warnRate),
        mode: nextMode,
      },
      { replace: true },
    );
  }

  function reset() {
    const fresh = { from: daysAgoISO(5), to: todayISO(), minUsers: DEFAULTS.minUsers, warnRate: DEFAULTS.warnRate };
    setDraft(fresh);
    setParams({}, { replace: true });
  }

  const isHourly = mode === 'hourly_average';

  return (
    <section className="page--wide join-dash">
      <header className="join-dash__top">
        <div>
          <div className="join-dash__eyebrow">Acquisition analytics</div>
          <h1 className="page-title">신규가입 전환 대시보드</h1>
          <p className="page-desc">
            신규가입 페이지 유니크 유저수와 실제 가입자수를 시간대별로 비교합니다.
          </p>
        </div>
        <div className={isHourly ? 'join-criteria join-criteria--hourly' : 'join-criteria'}>
          <span className="join-criteria__dot" />
          <span>
            {isHourly
              ? '기준: 동일 시간대 평균 전환율·평균 유입자 비교'
              : `기준: 방문 ${fmtNum(Number(minUsers))}명 이상, 전환율 ${warnRate}% 이하 주의`}
          </span>
        </div>
      </header>

      <div className="panel">
        <div className="panel__head">
          <div>
            <h2 className="panel__title">조회 조건</h2>
            <p className="panel__note">기간과 판정 기준을 설정한 뒤 분석 방식을 선택하세요.</p>
          </div>
          {dash.data && (
            <span className="panel__count">
              {fmtNum(dash.data.meta.rangeDayCount)}일 · {fmtNum(dash.data.summary.hourCount)}시간
            </span>
          )}
        </div>
        <div className="join-filter">
          <label className="field">
            <span className="field__label">시작일</span>
            <input
              className="input"
              type="date"
              value={draft.from}
              onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
            />
          </label>
          <label className="field">
            <span className="field__label">종료일</span>
            <input
              className="input"
              type="date"
              value={draft.to}
              onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
            />
          </label>
          <label className="field">
            <span className="field__label">최소 방문수</span>
            <input
              className="input"
              type="number"
              min={1}
              max={10000}
              value={draft.minUsers}
              onChange={(e) => setDraft((d) => ({ ...d, minUsers: e.target.value }))}
            />
          </label>
          <label className="field">
            <span className="field__label">주의 전환율(%)</span>
            <input
              className="input"
              type="number"
              min={0}
              max={100}
              step={0.1}
              value={draft.warnRate}
              onChange={(e) => setDraft((d) => ({ ...d, warnRate: e.target.value }))}
            />
          </label>
          <div className="join-filter__actions">
            <button type="button" className="btn btn--primary" onClick={() => search('standard')}>
              조회
            </button>
            <button
              type="button"
              className={isHourly ? 'btn btn--primary' : 'btn btn--ghost'}
              aria-pressed={isHourly}
              onClick={() => search('hourly_average')}
            >
              시간대별 평균
            </button>
            <button type="button" className="btn btn--ghost" onClick={reset}>
              초기화
            </button>
          </div>
        </div>
      </div>

      {dash.isPending ? (
        <Spinner />
      ) : dash.isError ? (
        <ErrorBox error={dash.error} onRetry={() => void dash.refetch()} />
      ) : (
        <DashboardBody data={dash.data} isHourly={isHourly} warnRate={Number(warnRate)} />
      )}
    </section>
  );
}

function DashboardBody({
  data,
  isHourly,
  warnRate,
}: {
  data: JoinDashboard;
  isHourly: boolean;
  warnRate: number;
}) {
  const { summary, meta } = data;
  // 원본은 시간대별 평균 모드로 들어오면 '문제' 탭과 '평균 유입자' 차트를 먼저
  // 보여준다 - 그 모드를 고른 이유가 곧 "평소와 다른 시간을 찾는 것" 이라서다.
  const [view, setView] = useState<ViewMode>(isHourly ? 'issue' : 'all');
  const [chart, setChart] = useState<ChartMode>(isHourly ? 'average' : 'conversion');

  const issueRows = data.rows.filter((r) => r.status !== 'ok');
  const rows = view === 'issue' ? issueRows : data.rows;

  /** 원본 rateClass - 심각/주의 기준으로 전환율 글자색을 가른다. */
  function rateClass(v: number | null): string {
    if (v === null || v === undefined) return '';
    if (v <= meta.criticalRate) return 'td-danger';
    if (v <= warnRate) return 'join-rate--warn';
    return 'td-ok';
  }

  const chartMax = {
    conversion: Math.max(1, ...data.hourBucket.map((b) => b.conversion ?? 0)),
    inflow: Math.max(1, ...data.hourBucket.map((b) => b.inflowRate ?? 0)),
    average: Math.max(1, ...data.hourBucket.map((b) => b.avgJoinTotal)),
  };

  function barWidth(value: number | null, field: ChartMode): string {
    const v = Number(value) || 0;
    if (v <= 0) return '0%';
    return `${Math.max(2, Math.round((v / chartMax[field]) * 100))}%`;
  }

  return (
    <>
      <div className="join-metrics">
        <Metric
          tone="primary"
          label="가입 페이지 유입"
          value={fmtNum(summary.joinTotal)}
          sub={`PC ${fmtNum(summary.joinP)} / Mobile ${fmtNum(summary.joinM)}`}
        />
        <Metric
          label="실제 가입자"
          value={fmtNum(summary.memberTotal)}
          sub={`PC ${fmtNum(summary.memberP)} / Mobile ${fmtNum(summary.memberM)}`}
        />
        <Metric
          tone={
            summary.conversion === null
              ? undefined
              : summary.conversion <= meta.criticalRate
                ? 'danger'
                : summary.conversion <= warnRate
                  ? 'warn'
                  : 'ok'
          }
          label="전체 전환율"
          value={fmtRate(summary.conversion)}
          sub="가입자 / 가입 페이지 유입"
        />
        <Metric
          label="소셜 전환 평균"
          value={fmtRate(summary.socialConversion)}
          sub={`일반 전환 평균 ${fmtRate(summary.normalConversion)}`}
        />
        <Metric
          tone="warn"
          label="주의/미달 시간"
          value={fmtNum(summary.warnCount + summary.lowSampleCount)}
          sub={`주의 ${fmtNum(summary.warnCount)} · 미달 ${fmtNum(summary.lowSampleCount)}`}
        />
        <Metric
          tone="danger"
          label="문제 의심"
          value={fmtNum(summary.dangerCount + summary.trackingIssueCount)}
          sub="가입 0건 또는 방문로그 누락"
        />
      </div>

      <div className="join-split">
        <div className="panel">
          <div className="panel__head">
            <div>
              <h2 className="panel__title">시간대별 상세</h2>
              <p className="panel__note">유입과 가입, 채널별 전환을 한 번에 확인합니다.</p>
            </div>
            <div className="tab-group">
              <button
                type="button"
                className={view === 'all' ? 'tab-btn tab-btn--active' : 'tab-btn'}
                aria-pressed={view === 'all'}
                onClick={() => setView('all')}
              >
                전체 {fmtNum(data.rows.length)}
              </button>
              <button
                type="button"
                className={view === 'issue' ? 'tab-btn tab-btn--active' : 'tab-btn'}
                aria-pressed={view === 'issue'}
                onClick={() => setView('issue')}
              >
                문제 {fmtNum(issueRows.length)}
              </button>
            </div>
          </div>

          <div className="table-wrap join-scroll">
            <table className="table table--dense">
              <thead>
                <tr>
                  <th>일자</th>
                  <th>시간</th>
                  <th>상태</th>
                  <th style={{ textAlign: 'right' }}>유입</th>
                  <th style={{ textAlign: 'right' }}>가입</th>
                  <th style={{ textAlign: 'right' }}>전환율</th>
                  <th>PC</th>
                  <th>Mobile</th>
                  <th>Social</th>
                  <th>Normal</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.date}_${r.hour}`} className={rowClass(r.status)}>
                    <td className="td-muted">{r.date}</td>
                    <td className="td-muted">{r.hourLabel}</td>
                    <td>
                      <StatusBadge row={r} />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <span className="join-value">
                        <b>{fmtNum(r.joinTotal)}</b>
                        {isHourly && (
                          <DeltaPill
                            current={r.joinTotal}
                            average={r.hourAvgJoinTotal}
                            kind="count"
                            tolerance={meta.hourlyAverageTolerance}
                          />
                        )}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <b>{fmtNum(r.memberTotal)}</b>
                    </td>
                    <td style={{ textAlign: 'right' }} className={rateClass(r.conversion)}>
                      <span className="join-value">
                        {fmtRate(r.conversion)}
                        {isHourly && r.conversion !== null && r.hourAvgConversion !== null && (
                          <DeltaPill
                            current={r.conversion}
                            average={r.hourAvgConversion}
                            kind="rate"
                            tolerance={meta.hourlyAverageTolerance}
                          />
                        )}
                      </span>
                    </td>
                    <td className="td-muted">
                      {fmtNum(r.joinP)}/{fmtNum(r.memberP)} ({fmtRate(r.pcConversion)})
                    </td>
                    <td className="td-muted">
                      {fmtNum(r.joinM)}/{fmtNum(r.memberM)} ({fmtRate(r.mobileConversion)})
                    </td>
                    <td className="td-muted">
                      {fmtNum(r.joinSocial)}/{fmtNum(r.social)} ({fmtRate(r.socialConversion)})
                    </td>
                    <td className="td-muted">
                      {fmtNum(r.joinNormal)}/{fmtNum(r.normal)} ({fmtRate(r.normalConversion)})
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={10} className="join-empty">
                      조회된 데이터가 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="join-side">
          <div className="panel">
            <div className="panel__head">
              <div>
                <h2 className="panel__title">우선 확인 시간</h2>
                <p className="panel__note">
                  {isHourly ? '현재값 / 동일 시간 평균' : '위험도와 낮은 전환율 순'}
                </p>
              </div>
              <span className="panel__count">상위 {fmtNum(data.issues.length)}건</span>
            </div>
            <div className="table-wrap join-scroll">
              <table className="table table--dense">
                <thead>
                  <tr>
                    <th>시간</th>
                    <th>상태</th>
                    <th style={{ textAlign: 'right' }}>유입</th>
                    <th style={{ textAlign: 'right' }}>가입</th>
                    <th style={{ textAlign: 'right' }}>전환율</th>
                  </tr>
                </thead>
                <tbody>
                  {data.issues.map((r) => (
                    <tr key={`issue_${r.date}_${r.hour}`} className={rowClass(r.status)}>
                      <td className="td-muted">
                        {r.date} {r.hourLabel}
                      </td>
                      <td>
                        <StatusBadge row={r} />
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span className="join-value">
                          <b>{fmtNum(r.joinTotal)}</b>
                          {isHourly && (
                            <DeltaPill
                              current={r.joinTotal}
                              average={r.hourAvgJoinTotal}
                              kind="count"
                              tolerance={meta.hourlyAverageTolerance}
                            />
                          )}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <b>{fmtNum(r.memberTotal)}</b>
                      </td>
                      <td style={{ textAlign: 'right' }} className={rateClass(r.conversion)}>
                        <span className="join-value">
                          {fmtRate(r.conversion)}
                          {isHourly && r.conversion !== null && r.hourAvgConversion !== null && (
                            <DeltaPill
                              current={r.conversion}
                              average={r.hourAvgConversion}
                              kind="rate"
                              tolerance={meta.hourlyAverageTolerance}
                            />
                          )}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {data.issues.length === 0 && (
                    <tr>
                      <td colSpan={5} className="join-empty">
                        문제 의심 시간이 없습니다.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="panel">
            <div className="panel__head">
              <div>
                <h2 className="panel__title">
                  {chart === 'inflow'
                    ? '시간대 평균 유입률'
                    : chart === 'average'
                      ? '시간대별 평균 유입자'
                      : '시간대 평균 전환율'}
                </h2>
                <p className="panel__note">
                  {chart === 'inflow'
                    ? '조회 기간 전체 유입 대비'
                    : chart === 'average'
                      ? '조회 기간 일평균'
                      : '조회 기간 합산 기준'}
                </p>
              </div>
              <div className="tab-group">
                {(
                  [
                    ['conversion', '전환율'],
                    ['inflow', '유입 비중'],
                    ['average', '평균 유입자'],
                  ] as [ChartMode, string][]
                ).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    className={chart === key ? 'tab-btn tab-btn--active' : 'tab-btn'}
                    aria-pressed={chart === key}
                    onClick={() => setChart(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="join-bars">
              {HOURS.map((hour) => {
                const b = data.hourBucket[hour]!;
                const value =
                  chart === 'conversion' ? b.conversion : chart === 'inflow' ? b.inflowRate : b.avgJoinTotal;
                return (
                  <div className="join-bar" key={`${chart}_${hour}`}>
                    <span className="join-bar__label">{String(hour).padStart(2, '0')}시</span>
                    <span className="join-bar__track">
                      <span
                        className={`join-bar__fill join-bar__fill--${chart}`}
                        style={{ width: barWidth(value, chart) }}
                      />
                    </span>
                    <span
                      className={
                        chart === 'conversion'
                          ? `join-bar__value ${rateClass(b.conversion)}`
                          : 'join-bar__value'
                      }
                    >
                      {chart === 'average' ? `${fmtAvg(b.avgJoinTotal)}명` : fmtRate(value)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head">
          <h2 className="panel__title">일자별 요약</h2>
          <span className="panel__count">일별 전환 흐름</span>
        </div>
        <div className="join-days">
          {data.daily.map((d) => (
            <article className="join-day" key={d.date}>
              <div className="join-day__head">
                <span className="join-day__date">{d.date}</span>
                <strong className={`join-day__rate ${rateClass(d.conversion)}`}>
                  {fmtRate(d.conversion)}
                </strong>
              </div>
              <div className="join-day__meta">
                유입 {fmtNum(d.joinTotal)} / 가입 {fmtNum(d.memberTotal)}
                <br />
                PC {fmtNum(d.memberP)} / Mobile {fmtNum(d.memberM)}
                <br />
                문제 {fmtNum(d.dangerCount)}h / 주의 {fmtNum(d.warnCount)}h
              </div>
            </article>
          ))}
          {data.daily.length === 0 && <div className="join-empty">조회된 데이터가 없습니다.</div>}
        </div>
      </div>
    </>
  );
}

function Metric({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  tone?: 'primary' | 'ok' | 'warn' | 'danger';
}) {
  return (
    <article className={tone ? `join-metric join-metric--${tone}` : 'join-metric'}>
      <span className="join-metric__label">{label}</span>
      <strong className="join-metric__value">{value}</strong>
      <span className="join-metric__sub">{sub}</span>
    </article>
  );
}

const STATUS_TONE: Record<JoinStatus, string> = {
  ok: 'badge--ok',
  warn: 'badge--warn',
  danger: 'badge--danger',
  tracking: 'badge--memo',
  low: 'badge--info',
};

/** 원본 statusTitle - 왜 이 상태인지(flags)를 배지 툴팁으로 보여준다. */
function StatusBadge({ row }: { row: JoinHourRow }) {
  return (
    <span
      className={`badge ${STATUS_TONE[row.status]}`}
      title={row.flags.length > 0 ? row.flags.join(' / ') : row.statusLabel}
    >
      {row.statusLabel}
    </span>
  );
}

function rowClass(status: JoinStatus): string | undefined {
  if (status === 'danger' || status === 'tracking') return 'join-row--danger';
  if (status === 'warn') return 'join-row--warn';
  return undefined;
}

/**
 * 시간대 평균과의 차이 배지. 원본 countDelta/rateDelta 와 같은 규칙:
 *  - 건수는 평균 대비 **비율**이 허용 오차(1%) 이내면 같다고 본다
 *  - 전환율은 **차이 자체**가 1%p 이내면 같다고 본다
 * 같다고 보면 배지를 아예 안 그린다 - 늘 붙어 있으면 눈에 안 들어온다.
 */
function DeltaPill({
  current,
  average,
  kind,
  tolerance,
}: {
  current: number;
  average: number;
  kind: 'count' | 'rate';
  tolerance: number;
}) {
  const round = (v: number) => Math.round((v + 1e-8) * 10) / 10;
  const diff = round(round(current) - round(average));

  let delta = diff;
  if (kind === 'count') {
    const avg = round(average);
    if (avg !== 0 && (Math.abs(diff) / Math.abs(avg)) * 100 <= tolerance) delta = 0;
  } else if (Math.abs(diff) <= tolerance) {
    delta = 0;
  }

  if (delta === 0) return null;

  const title =
    kind === 'count' ? `시간대 평균 ${fmtAvg(average)}명` : `시간대 평균 ${fmtRate(average)}`;
  const text =
    kind === 'count'
      ? `${fmtAvg(Math.abs(delta))}건 ${delta < 0 ? '감소' : '증가'}`
      : `${fmtAvg(Math.abs(delta))}% ${delta < 0 ? '하락' : '상승'}`;

  return (
    <span className={`join-delta join-delta--${delta < 0 ? 'down' : 'up'}`} title={title}>
      ({text})
    </span>
  );
}
